import axios from 'axios'
import retry, { exponentialDelay, isSafeRequestError } from 'axios-retry'
import * as Sentry from '@sentry/browser'
import isAfter from 'date-fns/isAfter'
import parseISO from 'date-fns/parseISO'
import normalizeUrl from 'normalize-url'

import config from '@/config'

import store from '@/store'

/**
 * Class for creating an instance of axios with custom config
 * https://github.com/axios/axios/tree/v0.x#creating-an-instance
 *
 * @param {string} options.baseUrl - `baseURL` will be prepended to `url` unless `url` is absolute
 */
class RequestClient {
	constructor(baseURL) {
		this.instance = axios.create({ baseURL })

		this.enableRetries()
	}

	onError(error) {
		Sentry.withScope((scope) => {
			scope.setExtras({
				data: error.config?.data ? JSON.stringify(error.config.data) : null,
				retries: JSON.stringify(error.config?.['axios-retry'] || {}),
				params: error.config?.params
			})
			scope.setTags({
				networkErrorType: error.name,
				networkErrorMessage: error.message,
				networkErrorCode: error.code,
				networkErrorMethod: error.config?.method,
				networkErrorBaseUrl: error.config?.baseURL,
				networkErrorUrl: error.config?.url,
				networkErrorResponseStatus: error.response?.status
			})
			Sentry.captureException(error)
		})

		throw error
	}

	enableRetries() {
		retry(this.instance, {
			shouldResetTimeout: true,
			retryDelay: exponentialDelay,
			retryCondition: function (error) {
				if (['ECONNABORTED', 'ERR_NETWORK'].includes(error.code)) {
					return true
				}

				// !error.response OR (status >= 500)
				// AND
				// methods: 'get' OR 'head' OR 'options'
				return isSafeRequestError(error)
			}
		})
	}
}

class FstrRequestClient extends RequestClient {
	constructor() {
		super(config('FstrAPIUrl'))

		this.instance.interceptors.request.use(this.requestFulfilled)
		this.instance.interceptors.response.use(null, this.responseRejected)
	}

	requestFulfilled = async (requestConfig) => {
		let isTokenDebugMode = false

		if ((requestConfig.url === 'user' && requestConfig.method === 'put') || requestConfig.url === 'user/check') {
			isTokenDebugMode = true
		}

		const token = await this.fetchToken(isTokenDebugMode)

		if (token) {
			requestConfig.headers['Authorization'] = `Bearer ${token}`
		}

		return requestConfig
	}

	responseRejected = async (err) => {
		const originalConfig = err.config
		if (err?.response?.status === 401 && !originalConfig._retry) {
			originalConfig._retry = true
			try {
				await this.fetchToken(false)
				return await this.instance(originalConfig)
			} catch (_error) {
				return Promise.reject(_error)
			}
		}
		return Promise.reject(err)
	}

	validateToken(token, expireAt) {
		return token && expireAt && isAfter(parseISO(expireAt), new Date())
	}

	async fetchToken(isTokenDebugMode) {
		const { token, refreshToken, expireAt } = store.getters.getAuthCredentials

		if (this.validateToken(token, expireAt)) {
			return token
		}

		if (isTokenDebugMode) {
			Sentry.withScope((scope) => {
				scope.setLevel(Sentry.Severity.Warning)
				scope.setExtras({
					expireAt: expireAt ?? null,
					newDate: new Date()?.toISOString(),
					tokenLength: token?.length ?? 0,
					refreshTokenLength: refreshToken?.length ?? 0,
					lsCacheLength: localStorage.getItem('lscache-vuex')?.length ?? 0,
					lsKeys: Object.keys(localStorage)
				})
				scope.setTags({
					tokenDebug: true
				})
				Sentry.captureMessage('Token refresh in /user or /user/check')
			})
		}

		const headers = {}
		const params = {}

		if (token && refreshToken) {
			params.refreshToken = refreshToken
			headers.Authorization = `Bearer ${token}`
		}

		const response = await request(config('FstrAPIUrl') + '/user/token', {
			headers,
			params,
			timeout: 15000
		})
		const responseData = response?.data ?? {}

		try {
			if (!responseData.expireAt || !responseData.token || !responseData.refreshToken) {
				throw new Error('Empty data in token response')
			}

			store.commit('setAuthCredentials', {
				token: responseData.token,
				refreshToken: responseData.refreshToken,
				expireAt: responseData.expireAt
			})

			if (!store.getters.getAuthCredentials.token) {
				throw new Error('Empty token in localStorage')
			}
		} catch (e) {
			Sentry.withScope((scope) => {
				scope.setExtras({
					...responseData,
					responseKeys: Object.keys(response)
				})
				scope.setTags({
					tokenEmptyFields: true
				})
				Sentry.captureException(e)
			})
		}

		return store.getters.getAuthCredentials.token
	}
}

const clients = {
	DEFAULT: new RequestClient(),
	FSTR_API: new FstrRequestClient(),
	SIMPLE_API: new RequestClient(config('SimpleAPIUrl')),
	ML_CHURN_API: new RequestClient(config('MlChurnPredictAPIUrl')),
	MARKETING_API: new RequestClient(config('MarketingAPIUrl'))
}

const getRequestNormalizedUrl = function (baseUrl, url) {
	let requestUrl = url

	if (baseUrl) {
		requestUrl = [baseUrl, url].join('/')
	}

	return normalizeUrl(requestUrl, { removeQueryParameters: true, stripProtocol: true, stripHash: true })
}

/**
 * @param {string} url
 * @param {string} clientName
 * @param {import('axios').Method} method
 * @param {import('axios').AxiosRequestConfig} options
 * @returns {import('axios').AxiosPromise}
 */
const request = async (url, { clientName = 'DEFAULT', method = 'get', ...options } = {}) => {
	const client = clients[clientName] ?? clients.DEFAULT
	const requestNormalizedUrl = getRequestNormalizedUrl(client.instance.defaults.baseURL, url)
	const spanId = `${method.toUpperCase()}_${requestNormalizedUrl}`

	window.ot.startSpan?.(spanId)

	return client
		.instance(url, { method, ...options })
		.then((data) => {
			window.ot.setNetworkRequestAttributes?.(spanId, { requestNormalizedUrl, ...data })
			return data
		})
		.then((response) => response.data)
		.catch((error) => {
			window.ot.setNetworkRequestAttributes?.(spanId, { requestNormalizedUrl, ...error })
			client.onError(error)
		})
		.finally(() => {
			window.ot.endSpan?.(spanId)
		})
}

export const fstrRequest = (url, options = {}) => {
	return request(url, { clientName: 'FSTR_API', ...options })
}

// eslint-disable-next-line import/no-unused-modules
export const simpleRequest = (url, options = {}) => {
	return request(url, { clientName: 'SIMPLE_API', ...options })
}

/**
 * Docs: https://churn-predict.dev.fstr.app/docs
 * @param {string} url
 * @param {import('axios').AxiosRequestConfig} options
 * @returns {import('axios').AxiosPromise}
 */
export const mlChurnPredictRequest = (url, options = {}) => {
	return request(url, { clientName: 'ML_CHURN_API', ...options })
}

/**
 * @param {string} url
 * @param {import('axios').AxiosRequestConfig} options
 * @returns {import('axios').AxiosPromise}
 */
export const marketingRequest = (url, options = {}) => {
	return request(url, { clientName: 'MARKETING_API', ...options })
}

export default request
